import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ControlProperty, ListRange, SearchCriteriaBase } from '../../../../../../common/models';
import { ClosedPeriodFrequency } from '../constants';
import { ListQueryParam } from '../../../../../../common/constants';
import { LocalDateRangeModel, LocalDateRangeInputModel, LocalDateModel } from '../../../../../models/local-date.model';
import { mustNotBeBlankValidator } from '../../../../../../common/utils';
import { fromZonedTime } from 'date-fns-tz';

export class ClosedPeriodModel {
  id?: number;
  frequency: ClosedPeriodFrequency;
  period: LocalDateRangeModel;
  name: string;

  constructor(params?: ClosedPeriodModel) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class ClosedPeriodForm {
  constructor(
    model: ClosedPeriodModel,
    public id: ControlProperty<number> = model.id,
    public name: ControlProperty<string> = [model.name, [Validators.required, mustNotBeBlankValidator]],
    public period: ControlProperty<LocalDateRangeInputModel> = [
      new LocalDateRangeInputModel(model.period),
      [dateRangeRequiredValidator()],
    ],
    public frequency: ControlProperty<ClosedPeriodFrequency> = [model.frequency, [Validators.required]],
  ) {}
}

export class ClosedPeriodListSearchCriteria extends SearchCriteriaBase {
  [ListQueryParam.Filter]: string;

  constructor(params?: ClosedPeriodListSearchCriteria | ListRange) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class ClosedPeriodRangeOutputModel {
  start: LocalDateModel;
  end: LocalDateModel;

  constructor(params?: { period?: LocalDateRangeInputModel; closedPeriod?: LocalDateRangeModel; timezone?: string }) {
    if (params) {
      const { period, closedPeriod } = params;
      const timezone = params.timezone ?? closedPeriod?.start?.timezone;

      const rawStart = period?.start ?? closedPeriod?.start?.timestamp;
      const rawEnd = period?.end ?? closedPeriod?.end?.timestamp;

      const start = this.toStartOfDayInTimezone(rawStart, timezone);
      const end = this.toEndOfDayInTimezone(rawEnd, timezone);

      this.start = {
        ...closedPeriod?.start,
        ...LocalDateModel.createLocalDateWithCalculatedOffset(start.valueOf(), timezone),
      };

      this.end = {
        ...closedPeriod?.end,
        ...LocalDateModel.createLocalDateWithCalculatedOffset(end.valueOf(), timezone),
      };
    }
  }

  private toStartOfDayInTimezone(value: Date | number | string, timezone?: string): Date {
    if (!timezone) {
      const date = new Date(value);
      date.setHours(0, 0, 0, 0);
      return date;
    }

    const dateOnly = this.toDateOnlyString(new Date(value));

    return fromZonedTime(`${dateOnly}T00:00:00.000`, timezone);
  }

  private toEndOfDayInTimezone(value: Date | number | string, timezone?: string): Date {
    if (!timezone) {
      const date = new Date(value);
      date.setHours(23, 59, 59, 999);
      return date;
    }

    const dateOnly = this.toDateOnlyString(new Date(value));

    return fromZonedTime(`${dateOnly}T23:59:59.999`, timezone);
  }

  private toDateOnlyString(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}

export function dateRangeRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as LocalDateRangeInputModel;
    if (!value || !value.start || !value.end) {
      return { required: true };
    }

    return null;
  };
}
